import { TypedDataDomain } from 'abitype';
import { match } from 'ts-pattern';
import {
  Hex,
  hashTypedData,
  hashDomain,
  getTypesForEIP712Domain,
  encodeAbiParameters,
  CompactSignature,
  Address,
} from 'viem';
import { z } from 'zod';

import { GenerateMomentSignatureVariables } from 'hooks/web3/use-generate-moment-signature';
import { getContractAddress } from 'lib/addresses';
import { ChainId } from 'lib/chains';
import { logger } from 'lib/logger';
import { ethersTransactionErrorSchema } from 'schemas/transaction';

import {
  GenerateSignatureOptions,
  SignatureListingType,
  SignatureApprovalType,
} from 'types/Signature';
import { EthersTransactionError } from 'types/Transactions';

import { getPath } from './router';
import { absoluteUrl } from './seo';

/**
 * @see https://github.com/GoogleChromeLabs/jsbi/issues/30
 */
BigInt.prototype['toJSON'] = function () {
  return this.toString();
};

const enforceTypedDataDomainShape = <T extends TypedDataDomain>(data: T): T => {
  return data;
};

export const getWorldsTypedDataDomain = (options: { chainId: ChainId }) => {
  const { chainId } = options;

  return enforceTypedDataDomainShape({
    name: 'Worlds',
    version: '1',
    chainId,
    verifyingContract: getContractAddress({
      chainId,
      contractName: 'worlds',
    }),
  } as const);
};

/**
 * TODO: (moment-based-take-rates) export from
 * npm package when typescript support lands
 */
export const signatureApprovalTypeMap: Record<SignatureApprovalType, number> = {
  SellerAllowlist: 0,
  Signature: 1,
} as const;

export const SIGNATURE_APPROVAL_TYPE = signatureApprovalTypeMap['Signature'];

/**
 * TODO: (moment-based-take-rates) export from
 * npm package when  typescript support lands
 */
export const signatureListingTypeMap: Record<SignatureListingType, number> = {
  NFT: 0,
  Collection: 1,
  Split: 2,
} as const;

/**
 * TODO: (moment-based-take-rates) export from
 * npm package when  typescript support lands
 */
export const EIP_712_TYPES_STRICT = {
  Worlds: {
    AuthorizeBySignature: {
      AuthorizeBySignature: [
        { name: 'worldId', type: 'uint256' },
        { name: 'seller', type: 'address' },
        { name: 'nftContract', type: 'address' },
        { name: 'nftTokenId', type: 'uint256' },
        { name: 'takeRateInBasisPoints', type: 'uint16' },
        { name: 'listingType', type: 'uint8' },
        { name: 'listingIdentifier', type: 'string' },
        { name: 'expiration', type: 'uint256' },
      ],
    },
  },
} as const;

export type WorldsEIP712Types = {
  AuthorizeBySignature: {
    worldId: number;
    seller: Address;
    nftContract: Address;
    nftTokenId: number;
    takeRateInBasisPoints: number;
    listingType: number;
    listingIdentifier: MomentListingIdentifier;
    expiration: number;
  };
};

const typedDataFieldSchema = z.array(
  z.object({
    name: z.string(),
    type: z.string(),
  })
);

export const EIP_712_TYPES = {
  Worlds: {
    AuthorizeBySignature: {
      AuthorizeBySignature: typedDataFieldSchema.parse(
        EIP_712_TYPES_STRICT.Worlds.AuthorizeBySignature.AuthorizeBySignature
      ),
    },
  },
};

type MomentListingIdentifier = `moment:${number}`;

export const buildMomentListingIdentifier = (
  momentId: number
): MomentListingIdentifier => `moment:${momentId}`;

export const mapTypedDomainToHash = (domain: TypedDataDomain): Hex => {
  return hashDomain({
    domain,
    types: {
      EIP712Domain: getTypesForEIP712Domain({ domain }),
    },
  });
};

export const mapSignatureOptionsToHash = (
  options: GenerateSignatureOptions
) => {
  return hashTypedData({
    domain: getWorldsTypedDataDomain({ chainId: options.chainId }),
    types: EIP_712_TYPES_STRICT.Worlds.AuthorizeBySignature,
    primaryType: 'AuthorizeBySignature',
    message: {
      worldId: BigInt(options.worldId),
      seller: options.publicKey,
      nftContract: options.contractAddress,
      nftTokenId: BigInt(options.tokenId),
      takeRateInBasisPoints: options.takeRateInBasisPoints,
      listingType: signatureListingTypeMap[options.listingType],
      listingIdentifier: buildMomentListingIdentifier(options.momentId),
      expiration: BigInt(options.expiration),
    },
  });
};

type SignTypedDataPayload = {
  domainSeparator: Hex;
  hashStructMessage: Hex;
};

export const buildSignTypedDataPayload = (
  options: GenerateSignatureOptions
): SignTypedDataPayload => {
  const typedDataDomain = getWorldsTypedDataDomain({
    chainId: options.chainId,
  });

  return {
    domainSeparator: mapTypedDomainToHash(typedDataDomain),
    hashStructMessage: mapSignatureOptionsToHash(options),
  };
};

export const encodePackedSignature = (options: {
  signature: CompactSignature;
  momentId: number;
  expiration: number;
  listingType: SignatureListingType;
}) => {
  return encodeAbiParameters(
    [
      { type: 'uint8' },
      { type: 'string' },
      { type: 'uint256' },
      { type: 'bytes32' },
      { type: 'bytes32' },
    ],
    [
      SIGNATURE_APPROVAL_TYPE,
      buildMomentListingIdentifier(options.momentId),
      BigInt(options.expiration),
      options.signature.r,
      options.signature.yParityAndS,
    ]
  );
};

export const mapTransactionErrorToLog = (error: EthersTransactionError) => {
  return {
    transaction: error.transaction,
    data: error.error.data.originalError.data,
  };
};

export const logSignatureError = (options: {
  error: Error;
  context: GenerateMomentSignatureVariables;
}) => {
  const parsedErrorSchema = ethersTransactionErrorSchema.safeParse(
    options.error
  );

  if (parsedErrorSchema.success) {
    logger.error('moment-signature-generation-error', {
      type: 'GAS_ESTIMATION_ERROR',
      context: 'client',
      error: mapTransactionErrorToLog(parsedErrorSchema.data),
      variables: options.context,
    });
  } else {
    logger.error('moment-signature-generation-error', {
      type: 'UNKNOWN_GAS_ESTIMATION_ERROR',
      context: 'client',
      error: JSON.parse(JSON.stringify(options.error)),
      variables: options.context,
    });
  }
};

type SignatureOptionsToAbsoluteUrlOptions = Pick<
  GenerateSignatureOptions,
  'listingType' | 'publicKey' | 'contractAddress' | 'tokenId'
>;

export const mapSignatureOptionsToAbsoluteUrl = (
  options: SignatureOptionsToAbsoluteUrlOptions
): string => {
  const appPath = match(options.listingType)
    .with('NFT', () =>
      getPath.nft.page({
        usernameOrAddress: options.publicKey,
        collectionSlugOrAddress: options.contractAddress,
        tokenId: options.tokenId,
      })
    )
    .with('Collection', 'Split', () =>
      getPath.collection.page(options.contractAddress)
    )
    .exhaustive();

  return absoluteUrl(appPath);
};
